import { ChangeEvent, KeyboardEventHandler, useCallback, useMemo, useRef } from 'react';
import styles from './SearchLayout.module.scss'
import Button from 'src/components/button/Button';
import SearchIcon from 'src/assets/icons/new_search.svg?react';
import ChevronDown from 'src/assets/icons/dropdown.svg?react';
import RewardIcon from 'src/assets/icons/grade_reward.svg?react';
import CalendarIcon from 'src/assets/icons/calendar.svg?react';
import InventoryIcon from 'src/assets/icons/inventory_icon.svg?react';
import { Dropdown } from "antd";
import type { MenuProps } from 'antd';
import { SelectOption } from '@src/types';
import moment from "moment";
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import './DropdownMunuStyle.css';
import { naAllProgramGradeLevels } from '@src/constants';


type Props = {
  focused: boolean;
  setFocused: (value: boolean) => void;
  showGradeMenu: boolean;
  setShowGradeMenu: (value: boolean) => void;
  showSchoolMenu: boolean;
  setShowSchoolMenu: (value: boolean) => void;
  showDatePicker: boolean;
  setShowDatePicker: (value: boolean) => void;
  search: string;
  filters: string[];
  filterOptions?: string[];
  selGrade?: SelectOption;
  gradeOptions: SelectOption[];
  handleSelectGrade: (grades: SelectOption) => void;
  handleChangeKeyword: (value: string) => void;
  handleApplySearch: () => void;
  startDate: Date | undefined;
  endDate: Date | undefined;
  dateRange: any[];
  onDateChange: (dates: any[]) => void;
  isDistrict?: boolean;
  selSchool?: SelectOption | undefined;
  schoolOptions?: SelectOption[];
  handleSelectSchool?: (school: SelectOption) => void;
}

function SearchLayout(props: Props) {
  const {
    focused,
    setFocused,
    showGradeMenu,
    setShowGradeMenu,
    showDatePicker,
    setShowDatePicker,
    search,
    selGrade,
    gradeOptions,
    startDate,
    endDate,
    dateRange,
    handleSelectGrade,
    handleChangeKeyword,
    handleApplySearch,
    onDateChange,
    isDistrict,
    selSchool,
    schoolOptions=[],
    handleSelectSchool,
    showSchoolMenu,
    setShowSchoolMenu,
  } = props

  const inputRef = useRef(null);

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  const handleGradeDropdownVisible = useCallback((visible: boolean) => {
    setShowGradeMenu(visible);
  }, [setShowGradeMenu]);

  const handleSchoolDropdownVisible = useCallback((visible: boolean) => {
    setShowSchoolMenu(visible);
  }, [setShowSchoolMenu]);

  const selectGrade: MenuProps['onClick'] = ({ key }) => {
    handleSelectGrade(gradeOptions[key]);
    setShowGradeMenu(false);
  };

  const selectSchool: MenuProps['onClick'] = ({ key }) => {
    setShowSchoolMenu(false)
    handleSelectSchool?.(schoolOptions[key])
};

  const gradeMenu = useMemo(( )=> {
    const items: MenuProps['items'] = gradeOptions.map((gradeLevel, index) => {
      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem} ${selGrade?.label === gradeLevel.label ? styles.select : ''}`}>
            {gradeLevel.label}
        </div>
        )
      }
    })
    return items
  }, [selGrade, gradeOptions])

  const schoolMenu = useMemo(( )=> {  // District Admin
    const items: MenuProps['items'] = (schoolOptions || []).map((gradeLevel, index) => {
        return {
            key: index,
            label: (
            <div className={`${styles.gradeDropDownMenuitem} ${selSchool?.label === gradeLevel.label ? styles.select : ''}`}>
                {gradeLevel.label}
            </div>
            )
        }
    })
    return items
}, [selSchool, schoolOptions])

  const dropDownViewStyle = useMemo(() => {
    const classes = [styles.selectGradeView];
    if (focused) {
      classes.push(styles.dropdownHidden);
    }
    return classes.join(' ');
  }, [focused])

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleChangeKeyword(e.target.value)
  }
 
  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (e: any) => {
    if ((e as KeyboardEvent).key === 'Enter') {
      handleApplySearch();
    }
  };

  const handleChange = (dates: any) => {    
    onDateChange(dates);
    setShowDatePicker(false);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(true);
  };

  const DateCustomInput = (
    <div className={dropDownViewStyle}>
      <div className={styles.gradeField}>
        <div className={styles.durationLabel}>
          {dateRange ? <div className={styles.badge}/> : null}
          <CalendarIcon width={28} height={28}/>
          <div className={styles.dateLabel}>Date: <span>{moment(startDate).format('YYYY-MM-DD')} → {moment(endDate).format('YYYY-MM-DD')}</span></div>
        </div>
      </div>
      <div className={[styles.dropdownToggle, showDatePicker ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
        <ChevronDown className={styles.icon}/>
      </div>
    </div>
  )
  
  return (
    <div className={styles.filterBanner}>
      <div className={styles.topicFilters}>
        <div className={styles.filterBox}>
          <div className={styles.emptyTitle}>
            <input 
              ref={inputRef} id="searchInput" placeholder='Search for Teacher Name'
              value={search} className={styles.input} type="text"
              onFocus={handleFocus} onBlur={handleBlur}
              onChange={onChange} onKeyDown={onKeyDown} />
          </div>
          <div className={styles.selectViews}>
            {isDistrict && <Dropdown menu={{ items: schoolMenu, onClick: selectSchool }} trigger={['click']} placement="bottom" onOpenChange={handleSchoolDropdownVisible} open={showSchoolMenu}>
              <div className={styles.selectGradeView}>
                <div className={styles.gradeField}>
                  <div className={styles.gradeLabel}><InventoryIcon /><span>{selSchool?.label}</span></div>
                </div>
                <div className={[styles.dropdownToggle, showSchoolMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                  <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown>}
            <Dropdown menu={{ items: gradeMenu, onClick: selectGrade }} trigger={['click']} placement="bottom" onOpenChange={handleGradeDropdownVisible} open={showGradeMenu}>
              <div className={dropDownViewStyle}>
                <div className={styles.gradeField}>
                  <div className={styles.gradeLabel}>
                    {selGrade && selGrade !== naAllProgramGradeLevels[0] ? <div className={styles.badge}/> : null}
                    <RewardIcon width={28} height={28}/><span>{selGrade ? selGrade?.label : 'All Grades'}</span>
                  </div>
                </div>
                <div className={[styles.dropdownToggle, showGradeMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                  <ChevronDown className={styles.icon}/>
                </div>
              </div>
            </Dropdown>
            <DatePicker
              selectsRange              
              startDate={startDate}
              endDate={endDate}
              selected={endDate}
              onChange={handleChange}
              onClickOutside={() => setShowDatePicker(false)}
              customInput={DateCustomInput}
              onCalendarOpen={toggleDatePicker}
              maxDate={new Date()}
            />
            <Button onClick={handleApplySearch} className={styles.searchButton} label={'Filter'} size='small' leftIcon={<SearchIcon className={styles.searchIcon} />} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default SearchLayout